/**
 * `@ch/client` — typed access to the ChargeHawk API.
 *
 * Every type in here comes from `schema.d.ts`, which is generated from
 * `cloud/openapi.json`, which is generated from the Fastify routes' Zod
 * schemas. Nothing along that chain is hand-maintained, which is the point:
 * `web/` cannot describe a request the server does not accept, because it has
 * no way to spell one.
 *
 * Regenerate with `pnpm openapi` after changing any route schema.
 *
 * The runtime is a thin `fetch` wrapper rather than a generated client. The API
 * is two endpoints on the same origin; pulling in a request library to call
 * them would be more code than it saves, and `fetch` keeps this package
 * dependency-free so the SPA bundle stays small.
 */
import type { paths } from "./schema.js";

export type { paths } from "./schema.js";

type JsonResponse<T> = T extends {
    responses: { 200: { content: { "application/json": infer B } } };
}
    ? B
    : never;

export type ChartResponse = JsonResponse<
    paths["/api/v1/devices/{deviceId}/chart"]["get"]
>;
export type ChartPoint = ChartResponse["points"][number];
export type ChartTier = ChartResponse["tier"];
export type UnplacedRegion = ChartResponse["unplaced"][number];

export type DeviceListResponse = JsonResponse<paths["/api/v1/devices"]["get"]>;
export type DeviceSummary = DeviceListResponse["devices"][number];

export type ChartQuery =
    paths["/api/v1/devices/{deviceId}/chart"]["get"]["parameters"]["query"];

export class ApiError extends Error {
    constructor(
        readonly status: number,
        readonly code: string,
        message: string,
    ) {
        super(message);
        this.name = "ApiError";
    }
}

export type ClientOptions = {
    /**
     * Defaults to the empty string — the SPA and the API share one origin
     * through Caddy, so requests are relative and no CORS config exists
     * anywhere in this repository. Override it only for a dev server or a
     * test harness.
     */
    baseUrl?: string;
    fetch?: typeof globalThis.fetch;
    /** Aborts an in-flight request; the chart passes one on every range change. */
    signal?: AbortSignal;
};

async function request<T>(
    path: string,
    query: Record<string, string | number | undefined>,
    options: ClientOptions,
): Promise<T> {
    const doFetch = options.fetch ?? globalThis.fetch;

    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value !== undefined) search.set(key, String(value));
    }
    const suffix = search.size > 0 ? `?${search.toString()}` : "";

    const response = await doFetch(`${options.baseUrl ?? ""}${path}${suffix}`, {
        headers: { accept: "application/json" },
        ...(options.signal ? { signal: options.signal } : {}),
    });

    if (!response.ok) {
        // The API returns `{ error, message }` for every failure it produces
        // itself. Anything else is a proxy or a network fault, and the status
        // line is all there is to report.
        const body = (await response.json().catch(() => null)) as {
            error?: string;
            message?: string;
        } | null;
        throw new ApiError(
            response.status,
            body?.error ?? "http_error",
            body?.message ?? response.statusText,
        );
    }

    return (await response.json()) as T;
}

export function createClient(options: ClientOptions = {}) {
    return {
        listDevices: (overrides: ClientOptions = {}): Promise<DeviceListResponse> =>
            request("/api/v1/devices", {}, { ...options, ...overrides }),

        getDeviceChart: (
            deviceId: string,
            query: { from: number; to: number; maxPoints?: number },
            overrides: ClientOptions = {},
        ): Promise<ChartResponse> =>
            request(
                `/api/v1/devices/${encodeURIComponent(deviceId)}/chart`,
                query,
                { ...options, ...overrides },
            ),
    };
}

export type ChargeHawkClient = ReturnType<typeof createClient>;
